import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref, onUpdated, computed, PropType } from 'vue';
import { SdsDynamicListResult } from '@/interfaces/model/general/sds';
import { DynamicList } from '@/interfaces/api/general/dynamicList';
import { SourceTypeId } from '@/lib/enum';
import { VForm } from 'vuetify/lib/components';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { i18n } from '@/plugins/vue-i18n';
import { fromISO } from '@/utils/datetime';
import { DateTime } from 'luxon';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsTableSection0DynamicList',
  props: {
  sdsId: {
    type: String,
    required: true,
    default: '',
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  lastCrossCheckEndedAt: {
    type: String,
    required: false,
    default: '',
  },
  dynamicLists: {
    type: Array as PropType<Array<DynamicList>>,
    required: true,
    default: () => [],
  },
  dynamicListResult: {
    type: Array as PropType<Array<SdsDynamicListResult>>,
    required: true,
    default: () => [],
  },
},
  emits: ["update:isValid", "editDynamicListResult", "addDynamicListResult", "deleteDynamicListResult"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:isValid', data: boolean): void;
  (
    e: 'editDynamicListResult',
    sdsId: string,
    dynamicListId: number,
    dynamicListResult: SdsDynamicListResult
  ): void;
  (e: 'addDynamicListResult', sdsId: string, dynamicListId: number): void;
  (e: 'deleteDynamicListResult', sdsId: string, dynamicListId: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);

const m = defineMessages({
  updateNote: 'チェックリストのメモを更新します。',
  addToList: 'チェックリストへ追加しますか？',
  target: '対象',
  removeFromList: 'チェックリストから削除しますか？',
  judgement: '該非',
});



const headerSection0 = [
  {
    text: $t(sharedMessages.name),
    value: 'name',
    sortable: false,
    width: '55%',
  },
  { text: $t(m.judgement), value: 'judgement', sortable: false, width: '5%' },
  {
    text: $t(sharedMessages.cause),
    value: 'cause',
    sortable: false,
    width: '25%',
  },
  {
    text: $t(sharedMessages.note),
    value: 'note',
    sortable: false,
    width: '15%',
  },
  { text: '', value: 'action', sortable: false, width: '5%' },
];

const form = ref<InstanceType<typeof VForm> | null>(null);
const dynamicListIndex = ref<number>(0);
const dynamicListResultNote = ref<SdsDynamicListResult>({});
const modalDynamicListResultNote = ref<boolean>(false);
const modalAddDynamicList = ref<boolean>(false);
const modalDeleteDynamicList = ref<boolean>(false);

const dynamicListId = computed(() => {
  if (props.dynamicLists.length === 0) {
    return 0;
  }
  return props.dynamicLists[dynamicListIndex.value].id;
});



const setDynamicListResult = (index): void => {
  const result = props.dynamicListResult.find(
    (item) => item.dynamic_list_id === props.dynamicLists[index].id
  );
  if (result) {
    dynamicListResultNote.value = structuredClone(result);
  }
};

const resetDynamicListResult = (): void => {
  dynamicListResultNote.value = {};
};

const openAddDynamicListModal = (index): void => {
  modalAddDynamicList.value = true;
  dynamicListIndex.value = index;
};

const openEditDynamicListResultNoteModal = (index): void => {
  modalDynamicListResultNote.value = true;
  dynamicListIndex.value = index;
};

const closeEditDynamicListResultNoteModal = (): void => {
  modalDynamicListResultNote.value = false;
};

const openDeleteDynamicListModal = (index): void => {
  modalDeleteDynamicList.value = true;
  dynamicListIndex.value = index;
};

const closeAddDynamicListModal = (): void => {
  modalAddDynamicList.value = false;
};

const closeDeleteDynamicListModal = (): void => {
  modalDeleteDynamicList.value = false;
};

const editDynamicListResultNote = (): void => {
  updateNoteInline(dynamicListId.value, dynamicListResultNote.value.note);
  emits(
    'editDynamicListResult',
    props.sdsId,
    dynamicListId.value,
    dynamicListResultNote.value
  );
  closeEditDynamicListResultNoteModal();
};

const addDynamicListResult = (): void => {
  emits('addDynamicListResult', props.sdsId, dynamicListId.value);
  closeAddDynamicListModal();
};

const deleteDynamicListResult = (): void => {
  emits('deleteDynamicListResult', props.sdsId, dynamicListId.value);
  closeDeleteDynamicListModal();
};

const includesJudge = (dynamicList): boolean => {
  return props.dynamicListResult
    .map((item) => item.dynamic_list_id)
    .includes(dynamicList.id);
};

const isPendingJudgement = (): boolean => {
  if (!props.lastCrossCheckEndedAt) return false;

  // Parse as JST and get next day 9 AM JST
  const endedAt = fromISO(props.lastCrossCheckEndedAt).setZone('Asia/Tokyo');
  const nextDay9AM = endedAt
    .plus({ days: 1 })
    .set({ hour: 9, minute: 0, second: 0, millisecond: 0 });

  // Compare with current time in JST
  const nowJST = DateTime.now().setZone('Asia/Tokyo');

  return nowJST < nextDay9AM;
};

const includesAndManualJudge = (dynamicList): boolean => {
  return props.dynamicListResult
    .filter((item) => item.source === SourceTypeId.USER.value)
    .map((item) => item.dynamic_list_id)
    .includes(dynamicList.id);
};

const findCause = (dynamicList): string => {
  const result = props.dynamicListResult.find(
    (item) => item.dynamic_list_id === dynamicList.id
  );
  if (result?.cause) {
    return result.cause;
  }
  return '-';
};

const findNote = (dynamicList): string => {
  const result = props.dynamicListResult.find(
    (item) => item.dynamic_list_id === dynamicList.id
  );
  if (result?.note) {
    return result.note;
  }
  return '-';
};

const updateNoteInline = (dynamicListId: number, newNote: string) => {
  const target = props.dynamicListResult.find(
    (item) => item.dynamic_list_id === dynamicListId
  );
  if (target) {
    target.note = newNote;
  }
};

onMounted((): void => {
  if (form.value) {
    form.value.validate();
  }
});

onUpdated((): void => {
  if (form.value) {
    form.value.validate();
  }
});

return { __sfc: true,$t, m, props, headerSection0, form, dynamicListIndex, dynamicListResultNote, modalDynamicListResultNote, modalAddDynamicList, modalDeleteDynamicList, dynamicListId, emits, setDynamicListResult, resetDynamicListResult, openAddDynamicListModal, openEditDynamicListResultNoteModal, closeEditDynamicListResultNoteModal, openDeleteDynamicListModal, closeAddDynamicListModal, closeDeleteDynamicListModal, editDynamicListResultNote, addDynamicListResult, deleteDynamicListResult, includesJudge, isPendingJudgement, includesAndManualJudge, findCause, findNote, updateNoteInline, sharedMessages }
}

})